import Carousel from '../carousel/carousel';

const defaults = {
  adaptiveHeight: false,
  cellSelector: '.gallery__slide',
  cellAlign: 'center',
  contain: false,
  prevNextButtons: false,
  pageDots: false,
  setGallerySize: true,
  groupCells: false,
  minSlides: 2,
  lazyLoad: true,
  arrowShape: false,
  enabledClass: 'gallery--enabled',
  destroyedClass: 'gallery--destroyed',
  pageNumberSelector: '[data-gallery-current]',
  buttonSelectors: {
    first: '[data-gallery-first]',
    prev: '[data-gallery-prev]',
    next: '[data-gallery-next]',
    last: '[data-gallery-last]'
  }
};

class Gallery {

  constructor (element, settings) {
    this.element = element;
    this.options = Object.assign({}, defaults, settings);
    this.wrapper = element.parentNode;

    this.keyCodes = {
      left: 37,
      up: 38,
      right: 39,
      prev: 38,
      next: 40,
      enter: 13,
      space: 32,
      home: 36,
      end: 35
    };

    this.allButtons = this.wrapper.querySelectorAll('button');
    this.pageNumber = this.wrapper.querySelector(this.options.pageNumberSelector);

    this.firstButton = this.wrapper.querySelector(this.options.buttonSelectors.first);
    this.prevButton = this.wrapper.querySelector(this.options.buttonSelectors.prev);
    this.nextButton = this.wrapper.querySelector(this.options.buttonSelectors.next);
    this.lastButton = this.wrapper.querySelector(this.options.buttonSelectors.last);

    this.firstPageClickListener = this.firstPageClickEvent.bind(this);
    this.lastPageClickListener = this.lastPageClickEvent.bind(this);
    this.nextPageClickListener = this.nextPageClickEvent.bind(this);
    this.prevPageClickListener = this.prevPageClickEvent.bind(this);
    this.buttonKeydownListener = this.buttonKeydownEvent.bind(this);
  }

  disableButton (element) {
    element.setAttribute('disabled', true);
    element.classList.add('pagination__jump-link--unlinked');
    element.classList.remove('pagination__jump-link--linked');
  }

  enableButton (element) {
    element.removeAttribute('disabled');
    element.classList.add('pagination__jump-link--linked');
    element.classList.remove('pagination__jump-link--unlinked');
  }

  addIndexesToButtons () {
    [...this.allButtons].map((item, index) => {
      item.buttonIndex = index;
    });
  }

  removeIndexesFromButtons () {
    [...this.allButtons].map((item, index) => {
      delete item.buttonIndex;
    });
  }

  updateButtonAttrs (index) {
    if (index === 0) {
      this.disableButton(this.firstButton);
      this.disableButton(this.prevButton);
      this.enableButton(this.nextButton);
      this.enableButton(this.lastButton);
    }

    if (index === this.maxIndex) {
      this.enableButton(this.firstButton);
      this.enableButton(this.prevButton);
      this.disableButton(this.nextButton);
      this.disableButton(this.lastButton);
    }

    if (index > 0 && index < this.maxIndex) {
      this.enableButton(this.firstButton);
      this.enableButton(this.prevButton);
      this.enableButton(this.nextButton);
      this.enableButton(this.lastButton);
    }
  }

  updatePageNumber (index) {
    const slideNumber = parseInt(index) + 1;
    this.pageNumber.innerHTML = ('0' + slideNumber).slice(-2);
  }

  updatePagination (index) {
    this.updatePageNumber(index);
    this.updateButtonAttrs(index);
  }

  addSelectEvent () {
    this.gallery.on('select', () => this.updatePagination(this.gallery.selectedIndex));
  }

  firstPageClickEvent () {
    this.gallery.selectCell(0);
  }

  prevPageClickEvent () {
    this.gallery.previous();
  }

  nextPageClickEvent () {
    this.gallery.next();
  }

  lastPageClickEvent () {
    this.gallery.selectCell(this.maxIndex);
  }

  focusSlider () {
    this.element.focus();
  }

  focusButton (index) {
    const buttonSelection = this.allButtons[index];
    if (buttonSelection && !buttonSelection.disabled) {
      buttonSelection.focus();
    }
  }

  buttonKeydownEvent (e) {
    const { buttonIndex } = e.target;

    switch (e.keyCode) {
      case this.keyCodes.up:
        this.focusSlider();
        break;
      case this.keyCodes.left:
      case this.keyCodes.prev:
        e.preventDefault();
        if (buttonIndex > 0) {
          this.focusButton(buttonIndex - 1);
        }
        break;
      case this.keyCodes.right:
      case this.keyCodes.next:
        e.preventDefault();
        if (buttonIndex < this.maxIndex) {
          this.focusButton(buttonIndex + 1);
        }
        break;
      case this.keyCodes.enter:
      case this.keyCodes.space:
        // if slider reaches the end, be a help and focus button in other direction
        // wait for flickity scrpt to return new slide value
        setTimeout(() => {
          const selectedIndex = this.gallery.selectedIndex;
          if (selectedIndex === 0) {
            this.focusButton(2);
          }
          if (selectedIndex === this.maxIndex) {
            this.focusButton(1);
          }
        }, 500);
        break;
    }
  }

  addEventListeners () {
    this.firstButton.addEventListener('click', this.firstPageClickListener, false);
    this.prevButton.addEventListener('click', this.prevPageClickListener, false);
    this.nextButton.addEventListener('click', this.nextPageClickListener, false);
    this.lastButton.addEventListener('click', this.lastPageClickListener, false);
    [...this.allButtons].map(buttonItem => {
      buttonItem.addEventListener('keydown', this.buttonKeydownListener, false);
    });
  }

  removeEventListeners () {
    this.firstButton.removeEventListener('click', this.firstPageClickListener);
    this.prevButton.removeEventListener('click', this.prevPageClickListener);
    this.nextButton.removeEventListener('click', this.nextPageClickListener);
    this.lastButton.removeEventListener('click', this.lastPageClickListener);
    [...this.allButtons].map(buttonItem => {
      buttonItem.removeEventListener('keydown', this.buttonKeydownListener);
    });
  }

  initOverlay () {
    const slides = this.element.querySelectorAll(this.options.cellSelector);
    [...slides].map(slide => {
      const figcaption = slide.querySelector('figcaption');
      if (figcaption) {
        slide.classList.add('has-figcaption');
        slide.addEventListener('click', function () {
          figcaption.classList.toggle('is-visible');
          slide.classList.toggle('has-overlay');
        });
      }
    });
  }

  init () {
    this.galleryInstance = new Carousel(this.element, this.options);
    this.galleryInstance.init();
    this.gallery = this.galleryInstance.getCarousel();
    this.maxIndex = this.gallery.slides.length - 1;
    this.addSelectEvent();
    this.addIndexesToButtons();
    this.addEventListeners();
    setTimeout(() => this.gallery.resize(), 50);
    this.initOverlay();
  }

  destroy () {
    this.gallery.destroy();
    this.removeIndexesFromButtons();
    this.removeEventListeners();
  }
}

export default Gallery;
